// After paying at Stripe, the buyer comes back to /?checkout_session=cs_... (from the website or from
// Settings → Subscription). The id has to survive signing in or creating an account, like an
// invitation token (invite.ts): it is kept in sessionStorage (this tab only) until the subscription
// is linked or the person moves on, and travels in the confirmation email's link when they sign up.

const KEY = 'mybooks.checkoutSession';
export const CHECKOUT_PARAM = 'checkout_session';
const ID_RE = /^cs_[A-Za-z0-9_]{1,250}$/;

function read(): string | null {
  try { return sessionStorage.getItem(KEY); } catch { return null; }
}

/**
 * The checkout session from the current URL, else the one kept from earlier. Taking it from the URL
 * also removes it from the address bar, so a reload or a shared link does not carry it on.
 */
export function pendingCheckout(
  location: Pick<Location, 'pathname' | 'search' | 'hash'> = window.location,
  history: Pick<History, 'replaceState'> | null = window.history,
): string | null {
  const params = new URLSearchParams(location.search);
  const fromUrl = params.get(CHECKOUT_PARAM);
  if (fromUrl && ID_RE.test(fromUrl)) {
    try { sessionStorage.setItem(KEY, fromUrl); } catch { /* storage blocked: we still have it for this page */ }
    if (history) {
      params.delete(CHECKOUT_PARAM);
      const rest = params.toString();
      history.replaceState(null, '', `${location.pathname}${rest ? `?${rest}` : ''}${location.hash}`);
    }
    return fromUrl;
  }
  const kept = read();
  return kept && ID_RE.test(kept) ? kept : null;
}

export function clearCheckout() {
  try { sessionStorage.removeItem(KEY); } catch { /* ignore */ }
}

/** The query to add to a sign-up confirmation link so the checkout comes back with the person. */
export function checkoutQuery(): string {
  const id = read();
  return id && ID_RE.test(id) ? `${CHECKOUT_PARAM}=${encodeURIComponent(id)}` : '';
}
